import {announce} from '@github-ui/aria-live'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useIsMutating, useMutation, useMutationState, useQueryClient, type QueryClient} from '@github-ui/react-query'
import {useNavigate} from '@github-ui/react-router'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Type-only form contract.
import type {EditMilestoneInput} from '@github-ui/repository-milestone/MilestoneEdit'
import {
  milestoneEditErrorMessage,
  updateMilestoneDetails,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Reuse the existing native request and error normalization.
} from '@github-ui/repository-milestone/use-update-milestone-details'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Reuse the existing native state request.
import {updateMilestoneState} from '@github-ui/repository-milestone/use-update-milestone-state'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Shared existing error copy.
import {LABELS} from '@github-ui/repository-milestone/labels'
import type {MilestoneState} from '@github-ui/server-persisted-queries/types/updateMilestoneMutation'

import {milestoneEditQueryKey} from './milestone-edit-route'
import {issuesLayoutRoute} from './issues-layout-route'
import {
  milestoneHeaderDocumentAdapter,
  milestoneHeaderQueryKey,
  milestoneIssuesQueryDeps,
  milestoneShowRoute,
} from './milestone-show-route'
import {syncMilestoneCaches} from './use-milestone-cache-sync'

const mutationKey = [...entityFieldsMutationKey(), 'milestoneEdit']

type MilestoneEditVariables = {
  owner: string
  repo: string
  number: number
  input: EditMilestoneInput
  state?: MilestoneState
  stayOnEdit: boolean
  operation: SetFieldsOperation
}

function targetsMilestone(variables: unknown, milestoneId: string | undefined) {
  if (milestoneId === undefined || !variables || typeof variables !== 'object' || !('operation' in variables)) {
    return false
  }
  const operation = variables.operation
  return !!operation && typeof operation === 'object' && 'id' in operation && operation.id === milestoneId
}

export function useMilestoneWritePending(milestoneId: string | undefined) {
  return (
    useIsMutating({
      mutationKey: entityFieldsMutationKey(),
      predicate: mutation => targetsMilestone(mutation.state.variables, milestoneId),
    }) > 0
  )
}

function canRenderShowFromCache(queryClient: QueryClient, owner: string, repo: string, number: number) {
  const keys = [
    milestoneHeaderQueryKey(owner, repo, number),
    milestoneShowRoute.queries.mainQuery.makeQueryKey(milestoneIssuesQueryDeps(owner, repo, number, false, 1)),
    issuesLayoutRoute.queries.mainQuery.makeQueryKey({pathname: `/${owner}/${repo}/issues/_layout`}),
  ]
  return keys.every(queryKey => {
    const query = queryClient.getQueryState(queryKey)
    return query?.status === 'success' && query.data != null && query.fetchStatus !== 'fetching'
  })
}

export function useMilestoneEditRequest(owner: string, repo: string, number: number) {
  const requests = useMutationState({
    filters: {mutationKey, exact: true},
    select: mutation => ({
      status: mutation.state.status,
      variables: mutation.state.variables as MilestoneEditVariables | undefined,
      error: mutation.state.error instanceof Error ? milestoneEditErrorMessage(mutation.state.error) : undefined,
    }),
  })
  return requests.findLast(request => {
    const variables = request.variables
    return variables?.owner === owner && variables.repo === repo && variables.number === number
  })
}

export function useOptimisticMilestoneEdit(owner: string, repo: string, number: number) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {mutate} = useMutation<SetFieldsOperation, Error, MilestoneEditVariables>({
    mutationKey,
    // Keep failed drafts recoverable after the originating form unmounts, for this app session.
    gcTime: Infinity,
    mutationFn: async variables => {
      const nameWithOwner = `${variables.owner}/${variables.repo}`
      if (variables.state) {
        const data = await updateMilestoneState({
          number: variables.number,
          nameWithOwner,
          state: variables.state,
        })
        const milestone = data.updateMilestone?.milestone
        if (!milestone || typeof milestone.id !== 'string' || typeof milestone.closed !== 'boolean') {
          throw new Error(LABELS.milestoneEditError)
        }
        return {type: 'setFields', id: milestone.id, fields: {closed: milestone.closed}}
      }
      const data = await updateMilestoneDetails(nameWithOwner, variables.number, variables.input)
      const milestone = data.updateMilestone?.milestone
      if (!milestone) throw new Error(LABELS.milestoneEditError)
      return {
        type: 'setFields',
        id: milestone.id,
        fields: {
          title: milestone.title,
          description: milestone.description,
          descriptionHTML: milestone.descriptionHTML,
          dueOn: milestone.dueOn,
          closed: milestone.closed,
          updatedAt: milestone.updatedAt,
          progressPercentage: milestone.progressPercentage,
        },
      }
    },
    onSuccess: async (operation, variables) => {
      await syncMilestoneCaches(queryClient, variables.owner, variables.repo, variables.number, {
        header: 'skip',
        edit: 'skip',
      })
      const editQueryKey = milestoneEditQueryKey(variables.owner, variables.repo, variables.number)
      await settleConfirmedOperation({
        queryClient,
        ownerId: operation.id,
        operation,
        targets: [
          {
            queryKey: milestoneHeaderQueryKey(variables.owner, variables.repo, variables.number),
            documentAdapter: milestoneHeaderDocumentAdapter,
            revalidate: true,
            refetchType: variables.stayOnEdit ? 'all' : undefined,
          },
          {queryKey: editQueryKey, revalidate: true},
        ],
      })
      queryClient.removeQueries({queryKey: editQueryKey, exact: true, type: 'inactive'})
      announce('Milestone changes saved.')
    },
    onError: error => {
      announce(milestoneEditErrorMessage(error), {assertive: true})
    },
  })

  return (input: EditMilestoneInput, state?: MilestoneState) => {
    const previous = queryClient
      .getMutationCache()
      .findAll({mutationKey, exact: true})
      .filter(mutation => {
        const variables = mutation.state.variables as MilestoneEditVariables | undefined
        return variables?.owner === owner && variables.repo === repo && variables.number === number
      })
    if (
      queryClient
        .getMutationCache()
        .findAll({mutationKey: entityFieldsMutationKey(), status: 'pending'})
        .some(mutation => targetsMilestone(mutation.state.variables, input.id))
    ) {
      return
    }
    const stayOnEdit =
      previous.some(mutation => mutation.state.status === 'error') ||
      !canRenderShowFromCache(queryClient, owner, repo, number)
    for (const mutation of previous) queryClient.getMutationCache().remove(mutation)
    const showUrl = `/${owner}/${repo}/milestone/${number}`
    mutate(
      {
        owner,
        repo,
        number,
        input,
        state,
        stayOnEdit,
        operation: {
          type: 'setFields',
          id: input.id,
          fields: state
            ? {closed: state === 'CLOSED'}
            : {title: input.title, description: input.description, dueOn: input.dueOn},
        },
      },
      // Observer callbacks are dropped on unmount, so completion cannot interrupt unrelated navigation.
      stayOnEdit ? {onSuccess: () => navigate(showUrl)} : undefined,
    )
    announce('Saving milestone changes.')
    if (!stayOnEdit) navigate(showUrl)
  }
}
